import { resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createBlock as _createBlock, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "max-w-[1180px]" }
const _hoisted_2 = { class: "text-sm font-semibold text-gray-700 mb-2" }
const _hoisted_3 = { class: "flex flex-col gap-4" }
const _hoisted_4 = { class: "border-t border-gray-100 pt-5" }
const _hoisted_5 = { class: "grid gap-5 sm:grid-cols-2" }
const _hoisted_6 = { class: "flex w-full justify-between" }

import _ from 'lodash'
import { computed, inject, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

import DropImpactWarningModal from '@/components/neue/DropImpactWarningModal.vue'
import { useFulfillmentWizardAccess } from '@/composables/useFulfillmentWizardAccess'
import { WIZARD_PATH } from '@/config/fulfillmentWizardSteps'
import { useToast } from '@/composables/useToast'
import { dateObjectToServerDay } from '@/helpers/dates'
import { affectedDropsFrom } from '@/helpers/dropImpact'
import { planLimitError } from '@/helpers/planLimits'
import { fulfillmentTypeLabel, fulfillmentTypeOptions } from '@/models/fulfillmentMethodTypes'
import api from '@/store/api'
import { mapRelationship, mapRelationships, mapType } from '@/store/mappers'


export default {
  __name: 'StoreFulfillment',
  props: ['id'],
  setup(__props) {

const bus = inject('bus')
const loading = ref(true)
const props = __props
const vuexStore = useStore()
const store = computed(() => mapRelationship({ type: 'store', id: props.id }))
const groupedMethods = computed(() => _.groupBy(mapType('fulfillment_method'), 'attributes.type'))
const waiting = ref({})

const modal = ref({ open: false })
const visibility = ref('')
const order = ref('')
const router = useRouter()
const wizardEnabled = useFulfillmentWizardAccess()

const startNew = () => {
  if (wizardEnabled.value) router.push({ path: WIZARD_PATH, query: { store: props.id } })
  else modal.value.open = true
}

onMounted(async () => {
  await Promise.all([
    api.call('GET', `stores/${props.id}?included=fulfillment_method_store_pairs`),
    api.call('GET', 'fulfillment_methods')
  ])
  loading.value = false
  load()
})

const load = () => {
  visibility.value = store.value.attributes.pickup_fulfillment_method_visibility
  order.value = store.value.attributes.fulfillment_method_order
}

const dirty = () => bus.emit('dirty/store', { from: 'fulfillment' })

bus.on('save/store', () => {
  api.call('PUT', `stores/${props.id}?included=none`, {
    store: {
      pickup_fulfillment_method_visibility: visibility.value,
      fulfillment_method_order: order.value
    }
  })
})

bus.on('discard/store', load)

const slots = computed(() =>
  mapType('fulfillment_slot').filter(x => {
    const method = mapRelationship(x.relationships?.fulfillment_method?.data)
    if (method) return methodActive(method)
  })
)

const slotsForDay = day => slots.value.filter(x => x.attributes.fulfillment_date === dateObjectToServerDay(day))

const getSlots = days => {
  for (const day of days) {
    for (const slot of slotsForDay(day)) {
      vuexStore.commit('DESTROY_OBJECT', { data: slot })
    }
  }
  const from = dateObjectToServerDay(days.at(0))
  const to = dateObjectToServerDay(days.at(-1))
  return api.call(
    'GET',
    `fulfillment_slots?date_from=${from}&date_to=${to}&included=duplicator,fulfillment_availabilities`
  )
}

// The method just removed from the store and the upcoming drops that removal
// left with no open date, so the merchant can undo it from the warning modal.
const impact = ref({ method: null, drops: [] })
const dismissImpact = () => (impact.value = { method: null, drops: [] })

const addPair = method =>
  api.call('POST', `stores/${props.id}/fulfillment_method_store_pairs`, {
    fulfillment_method_store_pair: {
      fulfillment_method_id: method.id
    }
  })

const removePair = (method, pair) =>
  api
    .call('DELETE', `stores/${props.id}/fulfillment_method_store_pairs/${pair.id}`, {}, { delete: false })
    .then(response => {
      impact.value = { method, drops: affectedDropsFrom(response) }
    })

const whileWaiting = (method, request) => {
  waiting.value[method.id] = true
  return request().finally(() => {
    waiting.value[method.id] = false
  })
}

const toggleMethod = method => {
  const pairs = mapRelationships(store.value?.relationships?.fulfillment_method_store_pairs?.data)
  const pair = pairs.find(x => x.attributes.fulfillment_method_id.toString() === method.id)

  whileWaiting(method, () => (pair ? removePair(method, pair) : addPair(method)))
}

const undoMethodChange = () => {
  const { method } = impact.value
  dismissImpact()
  whileWaiting(method, () => addPair(method))
}

const { showToast } = useToast()

const addMethod = () => {
  if (!modal.value.name || !modal.value.name.trim() || !modal.value.type) {
    return
  }

  api
    .call('POST', 'fulfillment_methods', {
      fulfillment_method: {
        name: modal.value.name.trim(),
        type: modal.value.type
      }
    })
    .then(() => {
      modal.value = { open: false }
    })
    .catch(error => showToast(planLimitError(error) || 'Could not create the fulfillment method', { type: 'error' }))
}

const methodActive = method => {
  const pairs = mapRelationships(store.value?.relationships?.fulfillment_method_store_pairs?.data)
  const result = pairs.some(x => x.attributes.fulfillment_method_id.toString() === method.id)
  if (waiting.value[method.id]) {
    return !result
  } else {
    return result
  }
}

return (_ctx, _cache) => {
  const _component_FulfillmentSlotCalendar = _resolveComponent("FulfillmentSlotCalendar")
  const _component_SettingsSection = _resolveComponent("SettingsSection")
  const _component_NeueToggle = _resolveComponent("NeueToggle")
  const _component_router_link = _resolveComponent("router-link")
  const _component_NeueButton = _resolveComponent("NeueButton")
  const _component_SelectInput = _resolveComponent("SelectInput")
  const _component_NeueLoading = _resolveComponent("NeueLoading")
  const _component_InputField = _resolveComponent("InputField")
  const _component_FormElement = _resolveComponent("FormElement")
  const _component_FormSection = _resolveComponent("FormSection")
  const _component_TheModal = _resolveComponent("TheModal")

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_NeueLoading, { loading: loading.value }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createVNode(_component_SettingsSection, {
            title: "Upcoming slots",
            description: "Every window customers can currently pick from, across all of this store's methods. Click a slot to edit it."
          }, {
            default: _withCtx(() => [
              _createVNode(_component_FulfillmentSlotCalendar, {
                slots: slots.value,
                store: store.value,
                editable: true,
                onGetSlots: getSlots
              }, null, 8, ["slots", "store"])
            ]),
            _: 1
          }),
          _createVNode(_component_SettingsSection, {
            title: "Methods",
            description: "Pickup, delivery and shipping options offered at checkout. Click a method to edit its schedule and areas in a new tab."
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groupedMethods.value, (methods, type) => {
                return (_openBlock(), _createElementBlock("div", { key: type }, [
                  _createElementVNode("div", _hoisted_2, _toDisplayString(_unref(fulfillmentTypeLabel)(type)), 1),
                  _createElementVNode("div", _hoisted_3, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(methods, (method) => {
                      return (_openBlock(), _createBlock(_component_router_link, {
                        key: method.id,
                        to: '/store/fulfillment-methods/' + method.id,
                        target: "_blank"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_NeueToggle, {
                            "model-value": methodActive(method),
                            name: method.attributes.name,
                            description: method.attributes.description,
                            disabled: waiting.value[method.id],
                            "onUpdate:modelValue": $event => (toggleMethod(method))
                          }, null, 8, ["model-value", "name", "description", "disabled", "onUpdate:modelValue"])
                        ]),
                        _: 2
                      }, 1032, ["to"]))
                    }), 128))
                  ])
                ]))
              }), 128)),
              _createElementVNode("div", _hoisted_4, [
                _createVNode(_component_NeueButton, {
                  type: "secondary",
                  onClick: startNew
                }, {
                  default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                    _createTextVNode("+ Method", -1)
                  ]))]),
                  _: 1
                })
              ])
            ]),
            _: 1
          }),
          _createVNode(_component_SettingsSection, {
            title: "What customers see",
            description: "How fulfillment days and pickup locations are listed to a customer at checkout."
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(_component_SelectInput, {
                  modelValue: visibility.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => ((visibility).value = $event)),
                  name: "Fulfillment day visibility",
                  options: [
              { name: 'Show all future days', value: 'all' },
              { name: 'Show only available days', value: 'available-only' }
            ],
                  onChange: dirty
                }, null, 8, ["modelValue"]),
                _createVNode(_component_SelectInput, {
                  modelValue: order.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => ((order).value = $event)),
                  name: "Pickup list order",
                  options: [
              { name: 'Manually set order in the dashboard', value: 'order' },
              { name: 'Alphabetical', value: 'alphabetical' },
              { name: 'Next available day first', value: 'upcoming-windows' }
            ],
                  onChange: dirty
                }, null, 8, ["modelValue"])
              ])
            ]),
            _: 1
          })
        ])
      ]),
      _: 1
    }, 8, ["loading"]),
    _createVNode(_component_TheModal, {
      "is-open": modal.value.open,
      "modal-title": "New fulfillment method",
      onCloseModal: _cache[5] || (_cache[5] = $event => (modal.value.open = false))
    }, {
      main: _withCtx(() => [
        _createVNode(_component_FormSection, null, {
          default: _withCtx(() => [
            _createVNode(_component_FormElement, null, {
              default: _withCtx(() => [
                _createVNode(_component_InputField, {
                  modelValue: modal.value.name,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = $event => ((modal.value.name) = $event)),
                  name: "Fulfillment method name"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_FormElement, null, {
              default: _withCtx(() => [
                _createVNode(_component_SelectInput, {
                  modelValue: modal.value.type,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = $event => ((modal.value.type) = $event)),
                  name: "Type",
                  options: _unref(fulfillmentTypeOptions)
                }, null, 8, ["modelValue", "options"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      footer: _withCtx(() => [
        _createElementVNode("div", _hoisted_6, [
          _createVNode(_component_NeueButton, {
            type: "secondary",
            onClick: _cache[4] || (_cache[4] = $event => (modal.value.open = false))
          }, {
            default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
              _createTextVNode("Cancel", -1)
            ]))]),
            _: 1
          }),
          _createVNode(_component_NeueButton, {
            type: modal.value.name && modal.value.type ? 'primary' : 'inactive',
            onClick: addMethod
          }, {
            default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
              _createTextVNode("Save", -1)
            ]))]),
            _: 1
          }, 8, ["type"])
        ])
      ]),
      _: 1
    }, 8, ["is-open"]),
    _createVNode(DropImpactWarningModal, {
      "is-open": impact.value.drops.length > 0,
      "affected-drops": impact.value.drops,
      "action-noun": "change",
      onUndo: undoMethodChange,
      onKeep: dismissImpact
    }, null, 8, ["is-open", "affected-drops"])
  ], 64))
}
}

}